import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

const DEBTS_KEY = ['debts'];

// ── Revolving drawdown queries ────────────────────────────────────────────────

export function useDebtDrawdowns(id) {
  return useQuery({
    queryKey: ['debts', id, 'drawdowns'],
    queryFn: () => api.get(`/debts/${id}/drawdowns`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

export function useDebtDrawdownState(id) {
  return useQuery({
    queryKey: ['debts', id, 'drawdown-state'],
    queryFn: () => api.get(`/debts/${id}/drawdown-state`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

export function useProvisionSchedule(id, { from, to } = {}) {
  return useQuery({
    queryKey: ['debts', id, 'provision-schedule', from, to],
    queryFn: () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return api.get(`/debts/${id}/provision-schedule${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

// ── Revolving drawdown mutations ──────────────────────────────────────────────

export function useCreateDrawdown() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ debtId, amount_cents, drawn_date, label, transaction_id }) =>
      api.post(`/debts/${debtId}/drawdowns`, { amount_cents, drawn_date, label, transaction_id }),
    onSuccess: (_data, { debtId }) => {
      qc.invalidateQueries({ queryKey: ['debts'] });
      qc.invalidateQueries({ queryKey: ['debts', debtId] });
      qc.invalidateQueries({ queryKey: ['debts', debtId, 'drawdowns'] });
      qc.invalidateQueries({ queryKey: ['debts', debtId, 'drawdown-state'] });
      qc.invalidateQueries({ queryKey: ['debts', debtId, 'provision-schedule'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['recurrences'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

export function useDeleteDrawdown() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ debtId, drawdownId }) =>
      api.delete(`/debts/${debtId}/drawdowns/${drawdownId}`),
    onSuccess: (_data, { debtId }) => {
      qc.invalidateQueries({ queryKey: ['debts'] });
      qc.invalidateQueries({ queryKey: ['debts', debtId] });
      qc.invalidateQueries({ queryKey: ['debts', debtId, 'drawdowns'] });
      qc.invalidateQueries({ queryKey: ['debts', debtId, 'drawdown-state'] });
      qc.invalidateQueries({ queryKey: ['debts', debtId, 'provision-schedule'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['recurrences'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useDebts() {
  return useQuery({
    queryKey: DEBTS_KEY,
    queryFn: () => api.get('/debts'),
    staleTime: 30_000,
  });
}

export function useDebt(id) {
  return useQuery({
    queryKey: ['debts', id],
    queryFn: () => api.get(`/debts/${id}`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

export function useDebtSuggestions(id) {
  return useQuery({
    queryKey: ['debts', id, 'suggestions'],
    queryFn: () => api.get(`/debts/${id}/suggestions`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

// ── Mutations — invalidate all related caches ────────────────────────────────

function useDebtMutation(mutateFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: mutateFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['recurrences'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

export function useCreateDebt() {
  return useDebtMutation((body) => api.post('/debts', body));
}

export function useUpdateDebt() {
  return useDebtMutation(({ id, ...body }) => api.patch(`/debts/${id}`, body));
}

export function useDeleteDebt() {
  return useDebtMutation((id) => api.delete(`/debts/${id}`));
}

export function useLinkTransactionToDebt() {
  return useDebtMutation(({ debtId, transactionId }) =>
    api.post(`/debts/${debtId}/link-transaction`, { transaction_id: transactionId })
  );
}

export function useBatchLinkTransactions() {
  return useDebtMutation(({ debtId, transactionIds }) =>
    api.post(`/debts/${debtId}/link-transactions`, { transaction_ids: transactionIds })
  );
}

export function useLinkRecurrenceToDebt() {
  return useDebtMutation(({ debtId, recurrenceId }) =>
    api.post(`/debts/${debtId}/link-recurrence`, { recurrence_id: recurrenceId })
  );
}

export function useUnlinkTransactionFromDebt() {
  return useDebtMutation(({ debtId, transactionId }) =>
    api.delete(`/debts/${debtId}/transactions/${transactionId}`)
  );
}

export function useUnlinkRecurrenceFromDebt() {
  return useDebtMutation(({ debtId, recurrenceId }) =>
    api.delete(`/debts/${debtId}/recurrences/${recurrenceId}`)
  );
}

// ── Allocations dette ↔ lot ──────────────────────────────────────────────────

/**
 * Les allocations invalident aussi le cache `lots` : la page d'un lot affiche
 * son financement, qui dérive des mêmes lignes.
 */
function useAllocationMutation(mutateFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: mutateFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debts'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}

export function useDebtAllocations(id) {
  return useQuery({
    queryKey: ['debts', id, 'allocations'],
    queryFn: () => api.get(`/debts/${id}/allocations`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

export function useCreateAllocation() {
  return useAllocationMutation(({ debtId, lotNumber, allocatedCents, notes }) =>
    api.post(`/debts/${debtId}/allocations`, {
      lot_number:      lotNumber,
      allocated_cents: allocatedCents,
      notes:           notes || null,
    })
  );
}

export function useDeleteAllocation() {
  return useAllocationMutation(({ debtId, allocationId }) =>
    api.delete(`/debts/${debtId}/allocations/${allocationId}`)
  );
}

/**
 * État de provision d'une dette classique : théorique vs solde réel du compte
 * dédié, avec l'écart chiffré. Distinct de useDebtDrawdownState (enveloppes).
 */
export function useDebtProvisionState(id) {
  return useQuery({
    queryKey: ['debts', id, 'provision-state'],
    queryFn: () => api.get(`/debts/${id}/provision-state`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}
